import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useProjects, useCreateProject, useDeleteProject, usePagination } from '../lib/hooks'
import { usePermissions } from '../contexts/IframeAuthProvider'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Input'
import { Modal, ConfirmModal, FormModal } from '../components/ui/Modal'
import { Badge, StatusBadge } from '../components/ui/Badge'
import { LoadingSpinner, LoadingCard } from '../components/ui/LoadingSpinner'
import { FileInput } from '../components/ui/FileUpload'
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Eye,
  Calendar,
  Users,
  FileText,
  AlertCircle
} from 'lucide-react'
import { formatDate, formatRelativeTime } from '../lib/utils'
import { type Project, type ProjectCreateRequest } from '../lib/api'

export const ProjectsPage: React.FC = () => {
  const permissions = usePermissions()
  const pagination = usePagination()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [extractionStatusFilter, setExtractionStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // API hooks
  const { data: projectsData, isLoading } = useProjects({
    query: searchQuery,
    status: statusFilter,
    extraction_status: extractionStatusFilter,
    skip: pagination.skip,
    limit: pagination.limit,
  })

  const createProjectMutation = useCreateProject()
  const deleteProjectMutation = useDeleteProject()

  // Update pagination when data changes
  React.useEffect(() => {
    if (projectsData) {
      pagination.setTotal(projectsData.total_count)
    }
  }, [projectsData, pagination.setTotal])


  const handleCreateProject = async (data: ProjectCreateRequest) => {
    try {
      const newProject = await createProjectMutation.mutateAsync(data)
      setShowCreateModal(false)
      
      // Navigate directly to the project page to see requirements being analyzed
      navigate(`/projects/${newProject.id}`)
    } catch (error) {
      // Error handled by mutation
    }
  }

  const handleDeleteProject = async () => {
    if (!selectedProject) return
    
    try {
      await deleteProjectMutation.mutateAsync(selectedProject.id)
      setShowDeleteModal(false)
      setSelectedProject(null)
    } catch (error) {
      // Error handled by mutation
    }
  }

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'analyzing', label: 'Analyzing' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'closed', label: 'Closed' },
  ]

  const extractionStatusOptions = [
    { value: '', label: 'All Extraction Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Projects</h1>
          <p className="text-gray-600">Manage RFP projects and requirements</p>
        </div>
        
        {permissions.canManageProjects && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
            
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
            />
            
            <Select
              placeholder="Filter by extraction status"
              value={extractionStatusFilter}
              onChange={(e) => setExtractionStatusFilter(e.target.value)}
              options={extractionStatusOptions}
            />
            
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('')
                setExtractionStatusFilter('')
              }}
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <LoadingCard key={i} />
          ))
        ) : projectsData?.data?.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter || extractionStatusFilter
                ? 'No projects match your current filters.'
                : 'Get started by creating your first project.'}
            </p>
            {permissions.canManageProjects && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            )}
          </div>
        ) : (
          projectsData?.data?.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={() => setSelectedProject(project)}
              onDelete={() => {
                setSelectedProject(project)
                setShowDeleteModal(true)
              }}
              canEdit={permissions.canManageProjects}
              canDelete={permissions.canManageProjects}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {projectsData && projectsData.data && projectsData.data.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-700">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, projectsData.total_count)} of {projectsData.total_count} projects
          </p>
          
          {projectsData.total_pages > 1 && (
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.prevPage}
                disabled={!pagination.hasPrevPage}
              >
                Previous
              </Button>
              
              <span className="text-sm text-gray-700">
                Page {pagination.page + 1} of {projectsData.total_pages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={pagination.nextPage}
                disabled={!pagination.hasNextPage}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateProject}
        loading={createProjectMutation.isLoading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        message={`Are you sure you want to delete "${selectedProject?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteProjectMutation.isLoading}
      />

    </div>
  )
}

// Project Card Component
interface ProjectCardProps {
  project: Project
  onEdit: () => void
  onDelete: () => void
  canEdit: boolean
  canDelete: boolean
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="card">
      <div className="card-content">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Link
                to={`/projects/${project.id}`}
                className="text-lg font-semibold text-gray-900 hover:text-blue-600"
              >
                {project.title}
              </Link>
              
              <StatusBadge status={project.status} />
              <StatusBadge status={project.extraction_status} />
            </div>
            
            <p className="text-gray-600 mb-3">{project.description}</p>
            
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                Due: {project.due_date ? formatDate(project.due_date) : 'N/A'}
              </div>
              
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1" />
                {project.vendor_count} vendors
              </div>
              
              <div className="flex items-center">
                <FileText className="w-4 h-4 mr-1" />
                {project.total_requirements} requirements
              </div>
              
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                Created {formatRelativeTime(project.created_at)}
              </div>
            </div>
          </div>
          
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            
            {showMenu && (
              <div className="absolute right-0 mt-1 bg-white border rounded-md shadow-lg z-10">
                <Link
                  to={`/projects/${project.id}`}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </Link>
                
                {canEdit && (
                  <button
                    onClick={onEdit}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </button>
                )}
                
                {canDelete && (
                  <button
                    onClick={onDelete}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Create Project Modal Component
interface CreateProjectModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ProjectCreateRequest) => void
  loading: boolean
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState<ProjectCreateRequest>({
    title: '',
    description: '',
    project_number: '',
    rfp_file_path: '',
    publish_date: '',
    due_date: '',
    auto_evaluate: false,
    notification_emails: [],
    cover_letter_only: false,
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file)
    if (file) {
      setFormData(prev => ({ ...prev, rfp_file_path: file.name }))
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Create New Project"
      loading={loading}
    >
      <div className="space-y-4">
        <Input
          label="Project Title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          required
        />
        
        <Input
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          required
        />
        
        <Input
          label="Project Number"
          value={formData.project_number}
          onChange={(e) => setFormData(prev => ({ ...prev, project_number: e.target.value }))}
        />
        
        <FileInput
          placeholder="Select RFP file..."
          onChange={handleFileChange}
        />
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Publish Date"
            type="date"
            value={formData.publish_date}
            onChange={(e) => setFormData(prev => ({ ...prev, publish_date: e.target.value }))}
          />
          
          <Input
            label="Due Date (Optional)"
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
          />
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="cover_letter_only"
            checked={formData.cover_letter_only}
            onChange={(e) => setFormData(prev => ({ ...prev, cover_letter_only: e.target.checked }))}
            className="w-4 h-4 accent-brand-700 border-slate-300 rounded"
          />
          <label htmlFor="cover_letter_only" className="ml-2 text-sm font-medium text-gray-900">
            Cover letter requirements only
          </label>
        </div>
      </div>
    </FormModal>
  )
}

export default ProjectsPage